/**
 * ReportLanguageSwitcher — viewer-only language toggle for /raportti.
 * Does NOT PATCH the instance language (unlike LanguageSwitcher).
 * Limited to the instance language plus languages with stored translations,
 * which can be any language (#1442): a few as buttons, more as a list of their
 * own names ("eesti", "suomi").
 */
import React from 'react';
import styled from '@emotion/styled';
import { languageName } from '@kuutio/shared/reportLanguages.js';
import { useReportEdits } from './ReportEditsContext';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const MAX_BUTTONS = 3;

const SwitcherContainer = styled.div`
  display: inline-flex;
  gap: 4px;
  padding: 2px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const LanguageButton = styled.button`
  padding: 4px 10px;
  background: ${props => (props.$active ? 'rgba(255, 255, 255, 0.95)' : 'transparent')};
  color: ${props => (props.$active ? '#111' : 'rgba(255, 255, 255, 0.75)')};
  border: none;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: ${props => (props.$active ? '700' : '500')};
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  &:hover {
    color: ${props => (props.$active ? '#111' : '#fff')};
    background: ${props => (props.$active ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.12)')};
  }
`;

const LanguageSelect = styled.select`
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;

  option {
    color: #111;
  }
`;

const shortLabel = (lang) => (lang === 'sv' ? 'SV-FI' : lang.toUpperCase());

export default function ReportLanguageSwitcher() {
  const { t } = useTranslation();
  const {
    availableLanguages,
    viewingLanguage,
    setViewLanguage,
    instanceId,
  } = useReportEdits();

  if (!instanceId || !availableLanguages || availableLanguages.length < 2) {
    return null;
  }

  if (availableLanguages.length > MAX_BUTTONS) {
    return (
      <LanguageSelect
        aria-label={t('report.topbar.language')}
        value={viewingLanguage}
        onChange={(e) => setViewLanguage(e.target.value)}
      >
        {availableLanguages.map((lang) => (
          <option key={lang} value={lang}>{languageName(lang, lang)}</option>
        ))}
      </LanguageSelect>
    );
  }

  return (
    <SwitcherContainer role="group" aria-label={t('report.topbar.language')}>
      {availableLanguages.map((lang) => (
        <LanguageButton
          key={lang}
          type="button"
          $active={viewingLanguage === lang}
          onClick={() => setViewLanguage(lang)}
          aria-pressed={viewingLanguage === lang}
          title={languageName(lang, lang)}
        >
          {shortLabel(lang)}
        </LanguageButton>
      ))}
    </SwitcherContainer>
  );
}
